import { useMemo, useEffect, useState } from 'react';
import axios from 'axios';
import { paths } from 'src/routes/paths';
import SvgColor from 'src/components/svg-color';
import { useTenant } from 'src/auth/context/firebase/TenantContext';
import { useAuthContext } from 'src/auth/hooks';
import axiosInstance from 'src/utils/axios';

// ----------------------------------------------------------------------

const icon = (name) => (
  <SvgColor src={`/assets/icons/navbar/${name}.svg`} sx={{ width: 1, height: 1 }} />
);

const ICONS = {
  job: icon('ic_job'),
  blog: icon('ic_blog'),
  chat: icon('ic_chat'),
  mail: icon('ic_mail'),
  user: icon('ic_user'),
  file: icon('ic_file'),
  lock: icon('ic_lock'),
  tour: icon('ic_tour'),
  order: icon('ic_order'),
  label: icon('ic_label'),
  blank: icon('ic_blank'),
  kanban: icon('ic_kanban'),
  folder: icon('ic_folder'),
  banking: icon('ic_banking'),
  booking: icon('ic_booking'),
  invoice: icon('ic_invoice'),
  product: icon('ic_product'),
  calendar: icon('ic_calendar'),
  disabled: icon('ic_disabled'),
  external: icon('ic_external'),
  menuItem: icon('ic_menu_item'),
  ecommerce: icon('ic_ecommerce'),
  analytics: icon('ic_analytics'),
  dashboard: icon('ic_dashboard'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { selectedTenant } = useTenant();
  const { user } = useAuthContext();
  const [tenantPages, setTenantPages] = useState([]);

  useEffect(() => {
    if (selectedTenant) {
      const fetchPages = async () => {
        try {
          const response = await axiosInstance.get(`${process.env.REACT_APP_API_BASE_URL}/api/Page/tenant/${selectedTenant}`);
          setTenantPages(response.data.filter(page => page.published));
        } catch (error) {
          console.error('Error fetching tenant pages:', error);
        }
      };

      fetchPages();
    }
  }, [selectedTenant]);

  const data = useMemo(() => {
    // Core navigation items, conditionally included if tenant pages match
    const coreItems = [
      
      { title: 'Dashboard', path: paths.dashboard.main, icon: ICONS.dashboard, key: 'dashboard' },
      { title: 'Executive KPI', path: paths.dashboard.two, icon: ICONS.ecommerce, key: 'executiveKPI' },
      { title: 'Zip', path: paths.dashboard.three, icon: ICONS.analytics, key: 'zip' },
      { title: 'Productivity', path: paths.dashboard.productivity, icon: ICONS.analytics, key: 'productivity' },
      { title: 'Adjustment', path: paths.dashboard.adjustment, icon: ICONS.analytics, key: 'adjustment' },
      { title: 'A/R', path: paths.dashboard.ar, icon: ICONS.analytics, key: 'ar' },
      { title: 'Denial', path: paths.dashboard.denial, icon: ICONS.ecommerce, key: 'denial' },
      { title: 'Action', path: paths.dashboard.action, icon: ICONS.analytics, key: 'action' },
    ];

    // Dynamically add tenant-specific items
    const dynamicItems = tenantPages.map(page => ({
      title: page.name,
      path: page.navRoute,
      icon: ICONS.analytics,
    }));

    // Check if core items should be included based on tenant pages
    const items = coreItems.reduce((acc, item) => {
      if (tenantPages.some(page => page.navRoute === item.path)) {
        acc.push(item);
      }
      return acc;
    }, []);

    return [
      {
        subheader: 'SalientView V1.8',
        items: [
          { title: 'Welcome', path: paths.dashboard.root, icon: ICONS.dashboard },
          ...dynamicItems,
          { title: 'Pro', path: paths.dashboard.pro, icon: ICONS.analytics, key: 'pro', roles: ['userWithProPage', 'userWithProPageOnly'] },
        ],
      },
      {
        items: [
          {
            title: 'Admin',
            path: paths.dashboard.admin.root,
            icon: ICONS.user,
            roles: ['admin'],
            children: [
              // { title: 'Create User', path: paths.dashboard.admin.root, roles: ['admin'] },
              { title: 'Tenants', path: paths.dashboard.admin.tenants, roles: ['admin'] },
              { title: 'Users', path: paths.dashboard.admin.five, roles: ['admin'] },
              { title: 'Articles', path: paths.dashboard.admin.articles, roles: ['admin'] },
              { title: 'Analytics', path: paths.dashboard.admin.six, roles: ['admin'] },
              { title: 'Logs', path: paths.dashboard.admin.Logs, roles: ['admin'] }
            ],
          },
        ],
      },
    ];
  }, [tenantPages]);

  return data;
}
